import { useEffect, useRef } from 'react';
import Intercom from '@intercom/messenger-js-sdk';
import { useAuth } from '@/hooks/useAuth';

export const INTERCOM_APP_ID = 'e2vhxk9f';

// Track if Intercom has been booted
let isIntercomBooted = false;

/**
 * Boot Intercom for anonymous users (login pages, etc.)
 * Call this before showing Intercom on unauthenticated pages
 */
export function bootIntercomAnonymous() {
  if (typeof window === 'undefined') return;
  
  try {
    // Shutdown any existing instance first
    if ((window as any).Intercom) {
      (window as any).Intercom('shutdown');
    }
    
    // Boot with just the app_id for anonymous visitor
    Intercom({
      app_id: INTERCOM_APP_ID,
      hide_default_launcher: true,
    });
    
    isIntercomBooted = true;
  } catch (error) {
    console.error('[Intercom] Failed to boot anonymous:', error);
  }
}

/**
 * Show Intercom messenger for anonymous users
 * Boots Intercom if not already booted
 */
export function showIntercomAnonymous() {
  if (typeof window === 'undefined') return;
  
  try {
    // Boot if not already booted
    if (!isIntercomBooted || !(window as any).Intercom) {
      bootIntercomAnonymous();
    }
    
    // Small delay to ensure boot completes
    setTimeout(() => {
      if ((window as any).Intercom) {
        (window as any).Intercom('show');
      }
    }, 100);
  } catch (error) {
    console.error('[Intercom] Failed to show anonymous:', error);
  }
}

/**
 * IntercomProvider initializes Intercom messenger with user data
 * Should be placed inside the authenticated area of the app
 */
export function IntercomProvider({ children }: { children: React.ReactNode }) {
  const { user, profile, organization } = useAuth();
  const hasBooted = useRef(false);

  useEffect(() => {
    if (user && profile) {
      try {
        // Shutdown any existing anonymous instance before booting with user data
        if ((window as any).Intercom && !hasBooted.current) {
          (window as any).Intercom('shutdown');
        }
        
        // Boot Intercom with user data
        Intercom({
          app_id: INTERCOM_APP_ID,
          user_id: user.id,
          name: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || undefined,
          email: user.email,
          created_at: user.created_at ? Math.floor(new Date(user.created_at).getTime() / 1000) : undefined,
          // Custom attributes
          company: organization ? {
            company_id: organization.id,
            name: organization.name,
          } : undefined,
          hide_default_launcher: true, // We'll use our own button
        });
        
        hasBooted.current = true;
        isIntercomBooted = true;
        
        // Update to ensure messenger is ready for interaction
        if ((window as any).Intercom) {
          (window as any).Intercom('update');
        }
      } catch (error) {
        console.error('[Intercom] Failed to boot with user:', error);
      }
    }

    // Cleanup on unmount
    return () => {
      if (hasBooted.current && (window as any).Intercom) {
        try {
          (window as any).Intercom('shutdown');
          hasBooted.current = false;
          isIntercomBooted = false;
        } catch (error) {
          // Ignore shutdown errors
        }
      }
    };
  }, [user, profile, organization]);

  return <>{children}</>;
}

/**
 * Hook to interact with Intercom
 */
export function useIntercom() {
  const show = () => {
    if (typeof window === 'undefined') return;
    
    const intercom = (window as any).Intercom;
    if (!intercom) {
      console.warn('[Intercom] Not initialized');
      return;
    }
    
    try {
      // Ensure messenger is ready by calling update first
      intercom('update');
      // Then show after a brief delay to allow update to complete
      setTimeout(() => {
        intercom('show');
      }, 50);
    } catch (error) {
      console.error('[Intercom] Failed to show:', error);
    }
  };

  const hide = () => {
    if (typeof window !== 'undefined' && (window as any).Intercom) {
      try {
        (window as any).Intercom('hide');
      } catch (error) {
        // Ignore hide errors
      }
    }
  };

  const showNewMessage = (message?: string) => {
    if (typeof window === 'undefined') return;
    
    const intercom = (window as any).Intercom;
    if (!intercom) return;
    
    try {
      intercom('update');
      setTimeout(() => {
        intercom('showNewMessage', message);
      }, 50);
    } catch (error) {
      console.error('[Intercom] Failed to show new message:', error);
    }
  };

  const update = (data: Record<string, unknown>) => {
    if (typeof window !== 'undefined' && (window as any).Intercom) {
      try {
        (window as any).Intercom('update', data);
      } catch (error) {
        // Ignore update errors
      }
    }
  };

  return {
    show,
    hide,
    showNewMessage,
    update,
  };
}
